import type { ThemeState } from '@atlaskit/tokens';
import { themeObjectToString } from '@atlaskit/tokens';

import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient';
import { getSessionData } from '@confluence/session-data/entry-points/SessionData';
import {
	API_DEFAULT_THEME_STATE,
	getEffectiveThemeState,
} from '@confluence/theming-utils/entry-points/getEffectiveTheme';

import { ErrorSubjects, ErrorSubjectIds, reportThemingError } from './errorHandling';
import type {
	WorkspaceThemePreferenceQuery as WorkspaceThemePreferenceQueryType,
	WorkspaceThemePreferenceQueryVariables,
} from './graphql/__types__/WorkspaceThemePreferenceQuery';
import type { WorkspaceThemePreferenceMutationVariables } from './graphql/__types__/WorkspaceThemePreferenceMutation';
import { WorkspaceThemePreferenceQuery } from './graphql/WorkspaceThemePreferenceQuery.graphql';
import { WorkspaceThemePreferenceMutation } from './graphql/WorkspaceThemePreferenceMutation.graphql';

const getWorkspaceARI = ({ cloudId, activationId }: { cloudId: string; activationId: string }) => {
	return `ari:cloud:confluence:${cloudId}:workspace/${activationId}`;
};

/**
 * Remote theme storage implementation using shared user preferences API.
 * This implementation is used when the 'shared-user-preferences' feature flag is enabled.
 * Uses Apollo client for GraphQL operations.
 */

// TODO (DST): There is a Relay implementation of Shared User Preferences operations in @atlassian/shared-user-preferences-api/workspace-theme-management, however it doesn't work in confluence. Please replace with the methods once the Apollo-to-Relay migration is completed.
export class RemoteThemeStorageShared {
	public static async getThemeRemote(): Promise<Partial<ThemeState>> {
		const sessionData = await getSessionData();
		const { cloudId, activationId, userId } = sessionData;

		if (!userId) {
			return getEffectiveThemeState(API_DEFAULT_THEME_STATE);
		}

		const workspaceAri = getWorkspaceARI({ cloudId, activationId });

		// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
		const { data, errors } = await getAGGClient().query<
			WorkspaceThemePreferenceQueryType,
			WorkspaceThemePreferenceQueryVariables
		>({
			query: WorkspaceThemePreferenceQuery,
			variables: {
				workspaceAri,
				accountId: userId,
			},
		});

		if (errors) {
			throw errors;
		}

		const themeString = data?.settings_userPreferencesResolved?.theme;

		return getEffectiveThemeState(themeString || API_DEFAULT_THEME_STATE);
	}

	public static async saveThemeRemote(theme: Partial<ThemeState>): Promise<void> {
		try {
			const sessionData = await getSessionData();
			const { cloudId, activationId, userId } = sessionData;

			if (!userId) {
				return;
			}

			const workspaceAri = getWorkspaceARI({ cloudId, activationId });

			// Serialize the theme state to a string using Atlaskit's serializer
			const themeSerialized = themeObjectToString(theme);

			// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
			await getAGGClient().mutate<WorkspaceThemePreferenceMutationVariables>({
				mutation: WorkspaceThemePreferenceMutation,
				variables: {
					workspaceAri,
					accountId: userId,
					theme: themeSerialized,
				},
			});
		} catch (e) {
			const attributes = {
				message: e.message,
				stack: e.stack,
				theme,
			};
			await reportThemingError(
				ErrorSubjects.remoteThemeStorage,
				ErrorSubjectIds.saveThemeRemote,
				attributes,
			);
		}
	}
}
